import { lazy } from 'react';
import type { DataRouteObject } from 'react-router';
import { Navigate } from 'react-router-dom';
import { AuthGuard } from 'src/components/guards/auth-guard';
import { Loadable } from 'src/components/loadable';
import { Layout } from 'src/layout/main-layout';

const PropertyMembersContainer = Loadable(
  lazy(() =>
    import(
      '../../features/property-members/site/containers/property-members'
    ).then(module => ({
      default: module.PropertyMembersContainer,
    })),
  ),
);

const PropertyMembers = Loadable(
  lazy(() =>
    import(
      '../../features/property-members/site/containers/property-members/property-members'
    ).then(module => ({
      default: module.PropertyMembersContainer,
    })),
  ),
);

const SiteMobileAppDisplayTabsContainer = Loadable(
  lazy(() =>
    import(
      '../../features/mobile-app-display/site/mobile-app-display-tabs'
    ).then(module => ({
      default: module.MobileAppDisplayTabsContainer,
    })),
  ),
);

const SiteMobileAppDisplayHomeScreenContainer = Loadable(
  lazy(() =>
    import(
      '../../features/mobile-app-display/site/home-screen/containers/home-screen'
    ).then(module => ({
      default: module.SiteMobileAppDisplayHomeScreenContainer,
    })),
  ),
);

const SiteMobileAppDisplayAmenitiesContainer = Loadable(
  lazy(() =>
    import(
      '../../features/mobile-app-display/site/amenities/containers/amenities'
    ).then(module => ({
      default: module.SiteMobileAppDisplayAmenitiesContainer,
    })),
  ),
);

const SiteMobileAppDisplayNewsfeedContainer = Loadable(
  lazy(() =>
    import(
      '../../features/mobile-app-display/site/newsfeed/containers/newsfeed'
    ).then(module => ({
      default: module.SiteMobileAppDisplayNewsfeedContainer,
    })),
  ),
);

const SiteMobileAppDisplayPlacesContainer = Loadable(
  lazy(() =>
    import(
      '../../features/mobile-app-display/site/places/containers/places'
    ).then(module => ({
      default: module.SiteMobileAppDisplayPlacesContainer,
    })),
  ),
);

const SiteMobileAppDisplayServicesContainer = Loadable(
  lazy(() =>
    import(
      '../../features/mobile-app-display/site/services/containers/services'
    ).then(module => ({
      default: module.SiteMobileAppDisplayServicesContainer,
    })),
  ),
);

const PushNotificationsContainer = Loadable(
  lazy(() =>
    import(
      '../../features/push-notifications/site/containers/push-notifications'
    ).then(module => ({
      default: module.default,
    })),
  ),
);

const PushNotificationDetailsContainer = Loadable(
  lazy(() =>
    import(
      '../../features/push-notifications/site/containers/[pushNotificationToken]'
    ).then(module => ({
      default: module.default,
    })),
  ),
);

const EditPushNotificationContainer = Loadable(
  lazy(() =>
    import(
      '../../features/push-notifications/site/containers/edit-push-notification'
    ).then(module => ({
      default: module.default,
    })),
  ),
);

const CreatePushNotificationContainer = Loadable(
  lazy(() =>
    import(
      '../../features/push-notifications/site/containers/create-push-notification'
    ).then(module => ({
      default: module.default,
    })),
  ),
);

const EventsContainer = Loadable(
  lazy(() =>
    import('../../features/events/site/containers/events').then(module => ({
      default: module.EventsContainer,
    })),
  ),
);

const EventDetailsContainer = Loadable(
  lazy(() =>
    import('../../features/events/site/containers/[eventToken]').then(
      module => ({
        default: module.EventDetailsContainer,
      }),
    ),
  ),
);

const CreateEventContainer = Loadable(
  lazy(() =>
    import('../../features/events/site/containers/create-event').then(
      module => ({
        default: module.CreateEventContainer,
      }),
    ),
  ),
);

const EditEventContainer = Loadable(
  lazy(() =>
    import('../../features/events/site/containers/edit-event').then(module => ({
      default: module.EditEventContainer,
    })),
  ),
);

const PostsContainer = Loadable(
  lazy(() =>
    import('../../features/posts/site/containers/posts/posts').then(module => ({
      default: module.PostsPage,
    })),
  ),
);

const PostDetailsContainer = Loadable(
  lazy(() =>
    import('../../features/posts/site/containers/posts/[postToken]').then(
      module => ({
        default: module.default,
      }),
    ),
  ),
);

const CreatePostContainer = Loadable(
  lazy(() =>
    import('../../features/posts/site/containers/posts/create').then(
      module => ({
        default: module.CreatePostContainer,
      }),
    ),
  ),
);

const EditPostContainer = Loadable(
  lazy(() =>
    import('../../features/posts/site/containers/posts/edit').then(module => ({
      default: module.EditPostContainer,
    })),
  ),
);

const PlacesTabsContainer = Loadable(
  lazy(() =>
    import('../../features/places/site/containers/places-tabs').then(
      module => ({
        default: module.PlacesTabsContainer,
      }),
    ),
  ),
);

const PlaceTypesContainer = Loadable(
  lazy(() =>
    import('../../features/places/site/containers/place-types').then(
      module => ({
        default: module.PlaceTypesContainer,
      }),
    ),
  ),
);

const PlacesContainer = Loadable(
  lazy(() =>
    import('../../features/places/site/containers/places').then(module => ({
      default: module.PlacesPage,
    })),
  ),
);

const PlaceDetailsContainer = Loadable(
  lazy(() =>
    import('../../features/places/site/containers/places/[placeToken]').then(
      module => ({
        default: module.PlaceDetailsContainer,
      }),
    ),
  ),
);

const EditPlaceContainer = Loadable(
  lazy(() =>
    import('../../features/places/site/containers/places/edit').then(
      module => ({
        default: module.EditPlaceContainer,
      }),
    ),
  ),
);

const CreatePlacesContainer = Loadable(
  lazy(() =>
    import('../../features/places/site/containers/places/create').then(
      module => ({
        default: module.CreatePlaceContainer,
      }),
    ),
  ),
);

const GuestsContainer = Loadable(
  lazy(() =>
    import('../../features/guests/site/containers/guests').then(module => ({
      default: module.GuestsContainer,
    })),
  ),
);

const Guests = Loadable(
  lazy(() =>
    import('../../features/guests/site/containers/guests/guests').then(
      module => ({
        default: module.GuestsContainer,
      }),
    ),
  ),
);

const Polls = Loadable(
  lazy(() =>
    import('../../features/surveys/site/containers/polls').then(module => ({
      default: module.PollsContainer,
    })),
  ),
);

const PollDetails = Loadable(
  lazy(() =>
    import('../../features/surveys/site/containers/[poll-id]').then(module => ({
      default: module.PollDetailsContainer,
    })),
  ),
);

const CreatePoll = Loadable(
  lazy(() =>
    import('../../features/surveys/site/containers/create-poll').then(
      module => ({
        default: module.CreatePollContainer,
      }),
    ),
  ),
);

const EditPoll = Loadable(
  lazy(() =>
    import('../../features/surveys/site/containers/edit-poll').then(module => ({
      default: module.EditPollContainer,
    })),
  ),
);

const PartnershipsContainer = Loadable(
  lazy(() =>
    import('../../features/partnerships/site/containers/partnerships').then(
      module => ({
        default: module.PartnershipsContainer,
      }),
    ),
  ),
);

const PartnershipDetailsContainer = Loadable(
  lazy(() =>
    import(
      '../../features/partnerships/site/containers/[partnershipToken]'
    ).then(module => ({
      default: module.PartnershipDetailsContainer,
    })),
  ),
);

const CreatePartnershipContainer = Loadable(
  lazy(() =>
    import(
      '../../features/partnerships/site/containers/create-partnership'
    ).then(module => ({
      default: module.CreatePartnershipContainer,
    })),
  ),
);

const EditPartnershipContainer = Loadable(
  lazy(() =>
    import('../../features/partnerships/site/containers/edit-partnership').then(
      module => ({
        default: module.EditPartnershipContainer,
      }),
    ),
  ),
);

const IssuesContainer = Loadable(
  lazy(() =>
    import('../../features/issues/site/containers/issues').then(module => ({
      default: module.SiteIssuesContainer,
    })),
  ),
);

const IssueDetailsContainer = Loadable(
  lazy(() =>
    import('../../features/issues/site/containers/issue-details').then(
      module => ({
        default: module.SiteIssueDetailsContainer,
      }),
    ),
  ),
);

const SiteAdministratorsContainer = Loadable(
  lazy(() =>
    import('../../features/administrators/site/containers').then(module => ({
      default: module.SiteAdministratorsContainer,
    })),
  ),
);

const ContactForm = Loadable(
  lazy(() =>
    import('../../features/contact-form/site/containers/contact-form').then(
      module => ({
        default: module.ContactFormContainer,
      }),
    ),
  ),
);

const AmenitiesTabsContainer = Loadable(
  lazy(() =>
    import('../../features/spaces/site/containers/spaces-tabs').then(
      module => ({
        default: module.SpacesTabsContainer,
      }),
    ),
  ),
);

const AmenitiesContainer = Loadable(
  lazy(() =>
    import('../../features/spaces/site/containers/spaces').then(module => ({
      default: module.SpacesContainer,
    })),
  ),
);

const BookingRulesContainer = Loadable(
  lazy(() =>
    import('../../features/spaces/site/containers/booking-rules').then(
      module => ({
        default: module.BookingRulesContainer,
      }),
    ),
  ),
);

const CreateAmenityContainer = Loadable(
  lazy(() =>
    import('../../features/spaces/site/containers/create-space').then(
      module => ({
        default: module.CreateSpaceContainer,
      }),
    ),
  ),
);
const EditAmenityContainer = Loadable(
  lazy(() =>
    import('../../features/spaces/site/containers/edit-space').then(module => ({
      default: module.EditSpaceContainer,
    })),
  ),
);

const AmenityDetailsContainer = Loadable(
  lazy(() =>
    import('../../features/spaces/site/containers/[spaceToken]').then(
      module => ({
        default: module.SpaceDetailsContainer,
      }),
    ),
  ),
);

const AssignBookingRulesContainer = Loadable(
  lazy(() =>
    import('../../features/spaces/site/containers/assign-booking-rules').then(
      module => ({
        default: module.AssignBookingRulesContainer,
      }),
    ),
  ),
);

const EditBookingRulesContainer = Loadable(
  lazy(() =>
    import('../../features/spaces/site/containers/edit-booking-rules').then(
      module => ({
        default: module.EditBookingRulesContainer,
      }),
    ),
  ),
);

const BookingsContainer = Loadable(
  lazy(() =>
    import('../../features/bookings/site/containers/bookings').then(module => ({
      default: module.BookingsContainer,
    })),
  ),
);

const CreateBookingContainer = Loadable(
  lazy(() =>
    import('../../features/bookings/site/containers/create-booking').then(
      module => ({
        default: module.CreateBookingContainer,
      }),
    ),
  ),
);

const FaqsContainer = Loadable(
  lazy(() =>
    import('../../features/faqs/site/containers/faqs').then(module => ({
      default: module.default,
    })),
  ),
);

const FaqDetailsContainer = Loadable(
  lazy(() =>
    import('../../features/faqs/site/containers/[faqToken]').then(module => ({
      default: module.default,
    })),
  ),
);

const CreateFaqContainer = Loadable(
  lazy(() =>
    import('../../features/faqs/site/containers/create-faq').then(module => ({
      default: module.default,
    })),
  ),
);

const EditFaqContainer = Loadable(
  lazy(() =>
    import('../../features/faqs/site/containers/edit-faq').then(module => ({
      default: module.default,
    })),
  ),
);

const routes: DataRouteObject[] = [
  {
    path: '/',
    id: 'property-members',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: '/property-members',
        path: '/',
        index: true,
        element: <Navigate to="/property-members" replace />,
      },
    ],
  },
  {
    id: 'property-members-container',
    path: 'property-members',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'property-members',
        path: '/property-members',
        element: <PropertyMembersContainer />,
        children: [
          {
            id: 'property-members',
            path: '/property-members/',
            element: <PropertyMembers />,
          },
        ],
      },
    ],
  },
  {
    id: 'guests-layout',
    path: 'guests',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'guests-container',
        path: '/guests',
        element: <GuestsContainer />,
        children: [
          {
            id: 'guests',
            path: '/guests/',
            element: <Guests />,
          },
        ],
      },
    ],
  },
  {
    id: 'site-mobile-app-display',
    path: 'mobile-app-display',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-mobile-app-display-redirect',
        path: '/mobile-app-display',
        element: <Navigate to="/mobile-app-display/home-screen" replace />,
      },
      {
        id: 'site-mobile-app-display-tabs-container',
        path: '/mobile-app-display',
        element: <SiteMobileAppDisplayTabsContainer />,
        children: [
          {
            id: 'site-mobile-app-display-home-screen',
            path: '/mobile-app-display/home-screen',
            element: <SiteMobileAppDisplayHomeScreenContainer />,
          },
          {
            id: 'site-mobile-app-display-amenities',
            path: '/mobile-app-display/amenities',
            element: <SiteMobileAppDisplayAmenitiesContainer />,
          },
          {
            id: 'site-mobile-app-display-newsfeed',
            path: '/mobile-app-display/newsfeed',
            element: <SiteMobileAppDisplayNewsfeedContainer />,
          },
          {
            id: 'site-mobile-app-display-places',
            path: '/mobile-app-display/places',
            element: <SiteMobileAppDisplayPlacesContainer />,
          },
          {
            id: 'site-mobile-app-display-services',
            path: '/mobile-app-display/services',
            element: <SiteMobileAppDisplayServicesContainer />,
          },
        ],
      },
    ],
  },
  {
    id: 'site-push-notifications',
    path: 'push-notifications',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-push-notifications-container',
        path: '/push-notifications',
        element: <PushNotificationsContainer />,
      },
      {
        id: 'site-push-notifications-details-container',
        path: '/push-notifications/:pushNotificationToken',
        element: <PushNotificationDetailsContainer />,
      },
      {
        id: 'site-push-notification-create-container',
        path: '/push-notifications/create',
        element: <CreatePushNotificationContainer />,
      },
      {
        id: 'site-push-notifications-edit-container',
        path: '/push-notifications/:pushNotificationToken/edit',
        element: <EditPushNotificationContainer />,
      },
    ],
  },
  {
    id: 'site-events',
    path: 'events',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-events-container',
        path: '/events',
        element: <EventsContainer />,
      },
      {
        id: 'site-event-details-container',
        path: '/events/:eventToken',
        element: <EventDetailsContainer />,
      },
      {
        id: 'site-event-create-container',
        path: '/events/create',
        element: <CreateEventContainer />,
      },
      {
        id: 'site-event-edit-container',
        path: '/events/:eventToken/edit',
        element: <EditEventContainer />,
      },
    ],
  },
  {
    id: 'site-news',
    path: 'news',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-news-container',
        path: '/news',
        element: <PostsContainer />,
      },
      {
        id: 'site-news-details-container',
        path: '/news/:postToken',
        element: <PostDetailsContainer />,
      },
      {
        id: 'site-news-create-container',
        path: '/news/create',
        element: <CreatePostContainer />,
      },
      {
        id: 'site-news-edit-container',
        path: '/news/:postToken/edit',
        element: <EditPostContainer />,
      },
    ],
  },
  {
    id: 'site-surveys',
    path: 'surveys',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-surveys-container',
        path: '/surveys',
        element: <Polls />,
      },
      {
        id: 'site-survey-details-container',
        path: '/surveys/:pollToken',
        element: <PollDetails />,
      },
      {
        id: 'site-survey-create-container',
        path: '/surveys/create',
        element: <CreatePoll />,
      },
      {
        id: 'site-survey-edit-container',
        path: '/surveys/:pollToken/edit',
        element: <EditPoll />,
      },
    ],
  },
  {
    id: 'amenities-container',
    path: 'amenities',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'amenities-tabs',
        path: '/amenities',
        element: <AmenitiesTabsContainer />,
        children: [
          {
            id: 'amenities',
            index: true,
            element: <AmenitiesContainer />,
          },
          {
            id: 'amenities-booking-rules',
            path: '/amenities/booking-rules',
            element: <BookingRulesContainer />,
          },
        ],
      },
      {
        id: 'site-amenity-details-container',
        path: '/amenities/:spaceToken',
        element: <AmenityDetailsContainer />,
      },
      {
        id: 'site-amenity-create-container',
        path: '/amenities/create',
        element: <CreateAmenityContainer />,
      },
      {
        id: 'site-amenity-edit-container',
        path: '/amenities/:spaceToken/edit',
        element: <EditAmenityContainer />,
      },
      {
        id: 'site-amenity-create-booking-rules-container',
        path: '/amenities/booking-rules/assign',
        element: <AssignBookingRulesContainer />,
      },
      {
        id: 'site-amenity-edit-booking-rules-container',
        path: '/amenities/booking-rules/:bookingRulesToken/edit',
        element: <EditBookingRulesContainer />,
      },
    ],
  },
  {
    id: 'places-container',
    path: 'places',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'places-tabs',
        path: '/places',
        element: <PlacesTabsContainer />,
        children: [
          {
            id: 'places',
            index: true,
            element: <PlacesContainer />,
          },
          {
            id: 'place-types',
            path: '/places/types',
            element: <PlaceTypesContainer />,
          },
        ],
      },
      {
        id: 'place-create',
        path: '/places/create',
        element: <CreatePlacesContainer />,
      },
      {
        id: 'place-details',
        path: '/places/:placeToken',
        element: <PlaceDetailsContainer />,
      },
      {
        id: 'place-edit',
        path: '/places/:placeToken/edit',
        element: <EditPlaceContainer />,
      },
    ],
  },
  {
    id: 'site-partnerships',
    path: 'partnerships',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-partnerships-container',
        path: '/partnerships',
        element: <PartnershipsContainer />,
      },
      {
        id: 'site-partnership-details-container',
        path: '/partnerships/:partnershipToken',
        element: <PartnershipDetailsContainer />,
      },
      {
        id: 'site-partnership-create-container',
        path: '/partnerships/create',
        element: <CreatePartnershipContainer />,
      },
      {
        id: 'site-partnership-edit-container',
        path: '/partnerships/:partnershipToken/edit',
        element: <EditPartnershipContainer />,
      },
    ],
  },
  {
    id: 'site-issues',
    path: 'issues',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-issues-container',
        path: '/issues',
        element: <IssuesContainer />,
      },
      {
        id: 'site-issue-details-container',
        path: '/issues/:issueToken',
        element: <IssueDetailsContainer />,
      },
    ],
  },
  {
    id: 'site-administrators',
    path: 'administrators',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-administrators-container',
        path: '/administrators',
        element: <SiteAdministratorsContainer />,
      },
    ],
  },
  {
    id: 'contact-form-container',
    path: 'contact-form',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'contact-form',
        index: true,
        element: <ContactForm />,
      },
    ],
  },
  {
    id: 'site-bookings',
    path: 'bookings',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'site-bookings-container',
        path: '/bookings',
        element: <BookingsContainer />,
      },
      {
        id: 'site-create-booking-container',
        path: '/bookings/create',
        element: <CreateBookingContainer />,
      },
    ],
  },
  {
    id: 'faqs',
    path: 'faqs',
    element: (
      <AuthGuard>
        <Layout />
      </AuthGuard>
    ),
    children: [
      {
        id: 'faqs-container',
        path: '/faqs',
        element: <FaqsContainer />,
      },
      {
        id: 'faq-details',
        path: '/faqs/:faqToken',
        element: <FaqDetailsContainer />,
      },
      {
        id: 'create-faq',
        path: '/faqs/create',
        element: <CreateFaqContainer />,
      },
      {
        id: 'edit-faq',
        path: '/faqs/:faqToken/edit',
        element: <EditFaqContainer />,
      },
    ],
  },
];

export default routes;
